import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { getRiskConsequencesName, getRiskEventsName, getRiskSourcesName, getScenarioStatusName } from 'src/api/types';
import { GTCheckBox, GTColumnSelectAnchor, GTTdm } from 'src/components/GenericTable';
import { formatDate, numeralFormat } from 'src/utils/misc';
import { filter } from 'lodash';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { gTCheckHandler } from 'src/components/GenericTable/util';
import { IncompleteStateMarkerNew, TextOverflowTooltip } from 'src/common';
import { Link } from 'react-router-dom';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { AssessmentsQuery } from 'src/graphql/_generated';

type Scenario = AssessmentsQuery['assessments'][number]['scenarios'][number];

type UseColumns = (a: { data?: Scenario[]; linkedScenarios: string[]; controlId: string }) => {
  columns: ColumnDef<Scenario>[];
  GTColumnSelectAnchorExported: JSX.Element;
  checked: string[];
  resetChecked: () => void;
};

const defaultColumns = ['status_scen', 'name', 'expected_loss', 'updated_at', 'edited_at'];

export const useScenarioColumns: UseColumns = ({ data = [], linkedScenarios, controlId }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    `${TableIds.controlLinkScenarioScenario}-columns`,
    defaultColumns,
  );
  const [checked, setChecked] = useState<string[]>([]);

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.tdm, GTInternalIds.checker];

    const list: ColumnDef<Scenario>[] = [
      {
        id: GTInternalIds.checker,
        header: ({ table }) => {
          const { isAllChecked, checkAllHandler, visibleRows } = gTCheckHandler(
            {
              checked,
              selector: 'id',
              table,
            },
            setChecked,
          );
          const isAllLinked = visibleRows.every((el) => linkedScenarios.some((linked) => linked === el));

          return (
            <GTCheckBox isChecked={isAllChecked || isAllLinked} onClick={checkAllHandler} disabled={isAllLinked} />
          );
        },
        cell: ({ row }) => {
          const id = row.original.id;
          const isLinked = linkedScenarios.some((el) => el === id);
          return (
            <GTCheckBox
              isChecked={checked.includes(id) || isLinked}
              onClick={() => {
                if (checked.includes(id)) {
                  setChecked(checked.filter((el) => el !== id));
                } else {
                  setChecked([...checked, id]);
                }
              }}
              disabled={isLinked}
            />
          );
        },
        size: 1,
        maxSize: 35,
        enableSorting: false,
      },
      {
        id: 'ux_id',
        header: 'Id',
        accessorFn: (row) => row.id || null,
        cell: (info) => info.getValue(),
      },
      {
        id: 'status_scen',
        header: 'Status',
        accessorFn: (row) => getScenarioStatusName(row.status),
        cell: (info) => info.getValue(),
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name,
        cell: (info) => (
          <TextOverflowTooltip>
            <Link
              className={'table-link'}
              to={`/risk-assessments/${info.row.original.assessment.id}/scenario/${info.row.original.id}`}
              target="_blank"
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: { value: ['Control', 'Modal:LinkScenariosToControl'], params: { id: controlId } },
                  destination: { value: ['Scenario'], params: { id: info.row.original.id } },
                  tags: ['SCENARIO', 'RMP'],
                });
              }}
            >
              {info.getValue() as string}
            </Link>
          </TextOverflowTooltip>
        ),
        sortUndefined: 'last',
        sortingFn: sortingFns.text,
      },
      {
        id: 'source',
        header: 'Source',
        accessorFn: (row) => getRiskSourcesName(row.source),
        cell: (info) => info.getValue(),
      },
      {
        id: 'event',
        header: 'Event',
        accessorFn: (row) => getRiskEventsName(row.event),
        cell: (info) => info.getValue(),
      },
      {
        id: 'consequence',
        header: 'Consequence',
        accessorFn: (row) => getRiskConsequencesName(row.consequence),
        cell: (info) => info.getValue(),
      },
      {
        id: 'risk_owner',
        header: 'Risk Owner',
        accessorFn: (row) => row.ownerName,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'expected_loss',
        header: 'Expected Loss',
        accessorFn: (row) => row.liveRisk?.expectedLoss || null,
        cell: (info) => numeralFormat(info.getValue() as number),
      },
      // {
      //   id: '_control_count',
      //   header: 'Linked Controls',
      //   accessorFn: (row) => row.riskManagementPlans?.length || 0,
      //   cell: (info) => info.getValue(),
      // },
      {
        id: 'risk_identified_date',
        header: 'Identified Date',
        accessorFn: (row) => row.riskIdentifiedDate || '',
        cell: (info) => <TLLink date={info.getValue() as string}>{formatDate(info.getValue() as string)}</TLLink>,
      },
      {
        id: 'updated_at',
        header: 'Last Updated',
        accessorFn: (row) => row.meta?.updatedAt || null,
        cell: ({ row }) => {
          const date = row.original.meta?.updatedAt;
          if (!date) return null;
          return (
            <TLLink date={date} newPage>
              {formatDate(date, { formatType: 'datetime' })}
            </TLLink>
          );
        },
      },
      {
        id: 'edited_at',
        header: 'Last Edited Date',
        accessorFn: (row) => row.editedAt || null,
        cell: ({ row }) => (
          <TLLink date={row.original.editedAt}>{formatDate(row.original.editedAt, { formatType: 'datetime' })}</TLLink>
        ),
      },
      {
        id: 'created_at',
        header: 'Created Date',
        accessorFn: (row) => row.meta?.createdAt || null,
        cell: ({ row }) => {
          const date = row.original.meta?.createdAt;
          if (!date) return null;
          return (
            <TLLink date={date} newPage>
              {formatDate(date, { formatType: 'datetime' })}
            </TLLink>
          );
        },
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm extraComponentLeft={<IncompleteStateMarkerNew scenario={row.original} />} />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, checked]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.controlLinkScenarioScenario}
      options={[
        { key: 'ux_id', title: 'Id' },
        { key: 'status_scen', title: 'Status' },
        { key: 'name', title: 'Name' },
        { key: 'source', title: 'Source' },
        { key: 'event', title: 'Event' },
        { key: 'consequence', title: 'Consequence' },
        {
          key: 'risk_owner',
          title: 'Risk Owner',
        },
        {
          key: 'expected_loss',
          title: 'Expected Loss',
        },
        // {
        //   key: '_control_count',
        //   title: 'Linked Controls',
        // },
        {
          key: 'risk_identified_date',
          title: 'Identified Date',
        },
        {
          key: 'updated_at',
          title: 'Last Updated',
        },
        {
          key: 'edited_at',
          title: 'Last Edited Date',
        },
        {
          key: 'created_at',
          title: 'Created Date',
        },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return { columns, GTColumnSelectAnchorExported, checked, resetChecked: () => setChecked([]) };
};
